import React, { useState } from "react";
import { X, Save, UserCircle2, Key } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const placeholderPassword = () => {
  const b = new Uint32Array(4);
  crypto.getRandomValues(b);
  return `Tmp${Array.from(b, (x) => x.toString(36)).join("")}9`;
};

const sendInvite = async (id) => {
  try {
    const r = await api.post(`/users/${id}/send-invite`);
    toast.success(`Davet e-postası gönderildi · ${r.data.email}`);
    return true;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Davet e-postası gönderilemedi");
    return false;
  }
};

export default function UserModal({ editing, personnel, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    email: editing?.email || "",
    name: editing?.name || "",
    role: editing?.role || "personel",
    personnel_id: editing?.personnel_id || "",
    password: "",
    aktif: editing?.aktif !== false,
    send_invite: false,
  }));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email zorunlu"); return; }
    if (!editing && !form.password && !form.send_invite) { toast.error("Yeni kullanıcı için şifre girin ya da davet e-postası seçin"); return; }
    if (form.password && (form.password.length < 10 || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(form.password) || !/\d/.test(form.password))) {
      toast.error("Şifre en az 10 karakter olmalı ve en az bir harf ile bir rakam içermeli"); return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          personnel_id: form.personnel_id || null,
          aktif: form.aktif,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
        if (form.send_invite) await sendInvite(editing.id);
      } else {
        const r = await api.post("/users", {
          email: form.email.trim(),
          password: form.password || placeholderPassword(),
          name: form.name,
          role: form.role,
          personnel_id: form.personnel_id || null,
        });
        toast.success("Kullanıcı eklendi");
        if (form.send_invite && !(await sendInvite(r.data.id))) toast.info("Kullanıcı oluşturuldu; daveti daha sonra Düzenle ekranından tekrar gönderebilirsiniz");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="user-modal">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <UserCircle2 size={18} /> {editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
          </h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="E-posta *">
            <input
              data-testid="user-email-input"
              type="email"
              required
              disabled={!!editing}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input disabled:bg-slate-100 disabled:text-slate-500"
            />
          </Field>
          <Field label="Ad Soyad">
            <input
              data-testid="user-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Rol *">
            <select
              data-testid="user-role-select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="input"
            >
              <option value="yonetici">Yönetici</option>
              <option value="muhasebeci">Muhasebeci</option>
              <option value="muhasebe_stj">Muhasebe Stj.</option>
              <option value="junior_muhasebe">Junior Muhasebe</option>
              <option value="personel">Personel</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label={form.role === "personel" ? "Personel Kaydı (opsiyonel)" : "Bağlı Personel (opsiyonel)"}>
            <select
              data-testid="user-personnel-select"
              value={form.personnel_id}
              onChange={(e) => setForm({ ...form, personnel_id: e.target.value })}
              className="input"
            >
              <option value="">— Bağlı değil —</option>
              {personnel.map((p) => (
                <option key={p.id} value={p.id}>{p.ad_soyad} ({p.sicil_no})</option>
              ))}
            </select>
          </Field>
          <Field label={form.send_invite ? "Şifre (davet gönderilecek, gerekmez)" : editing ? "Yeni Şifre (boş: değiştirme)" : "Şifre *"}>
            <div className="relative">
              <Key size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="user-password-input"
                type="password"
                minLength={10}
                required={!editing && !form.send_invite}
                value={form.password}
                disabled={form.send_invite}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="input pl-9"
              />
            </div>
          </Field>
          {form.role !== "admin" && (
            <label className="flex items-start gap-2 text-sm cursor-pointer bg-indigo-50 border border-indigo-200 rounded-lg p-2.5">
              <input type="checkbox" data-testid="user-send-invite" className="mt-0.5" checked={form.send_invite}
                onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} />
              <span>
                <span className="font-semibold text-indigo-900">Davet e-postası gönder</span>
                <span className="block text-[11px] text-indigo-700">Geçici şifre e-postayla gider; kişi ilk girişte kendi şifresini belirler. Yukarıdaki şifre alanı bu durumda kullanılmaz.</span>
              </span>
            </label>
          )}
          {editing && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.aktif}
                onChange={(e) => setForm({ ...form, aktif: e.target.checked })}
              />
              Hesap aktif
            </label>
          )}
        </div>
        <div className="p-5 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50">İptal</button>
          <button
            data-testid="user-save-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
      <style>{`.input { width: 100%; padding: 10px 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .input:focus { border-color: #0F172A; box-shadow: 0 0 0 2px rgba(15,23,42,0.1); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
